import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createAppQueryClient } from "./lib/queryClient";
import { HelmetProvider } from "react-helmet-async";
import { useAuthStore } from "./store/useAuthStore";
import Layout from "./components/Layout";
import AuthGuard from "./components/AuthGuard";
import Login from "./features/auth/pages/Login";
import Register from "./features/auth/pages/Register";
import ResetPasswordPage from "./features/auth/pages/ResetPasswordPage";
import ProfilePage from "./features/profile/pages/ProfilePage";
import LandingPage from "./features/core/pages/LandingPage";
import PrivacyPage from "./features/core/pages/PrivacyPage";
import TermsPage from "./features/core/pages/TermsPage";
import LegalCombinedPage from "./features/core/pages/LegalCombinedPage";
import AgeVerificationPage from "./features/auth/verification/AgeVerificationPage";
import InvitePage from "./features/core/pages/InvitePage";
import { IdentityReviewPage } from "./features/admin/pages/IdentityReviewPage";
import { RedemptionCodesPage } from "./features/admin/pages/RedemptionCodesPage";
import RedeemPage from "./features/redeem/RedeemPage";
import SupportersPage from "./features/redeem/SupportersPage";
import SupportPage from "./features/core/pages/SupportPage";
import RefundPolicyPage from "./features/core/pages/RefundPolicyPage";
import DownloadPage from "./features/core/pages/DownloadPage";
import { WEB_AUTH_UI_ENABLED } from "./config/publicConfig";
import { AdminLoginPage } from "./features/admin/pages/AdminLoginPage";
import {
  AdminLayout,
  AdminDashboardPage,
} from "./features/admin/pages/AdminDashboardPage";


// Handle Supabase Auth Redirects (e.g. Password Reset)
function AuthRedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for hash parameters from Supabase (e.g. #access_token=...&type=recovery)
    const hash = location.hash;
    if (hash && hash.includes("type=recovery")) {
      navigate("/reset-password");
    }
  }, [location, navigate]);

  return null;
}

function GlobalAuthInit() {
  const { initialize, initialized } = useAuthStore();

  useEffect(() => {
    if (!initialized) {
      initialize();
    }
  }, [initialize, initialized]);

  return null;
}

const queryClient = createAppQueryClient();

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <GlobalAuthInit />
        <BrowserRouter>
          <AuthRedirectHandler />
          <Routes>
            {/* Public Pages */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/refund" element={<RefundPolicyPage />} />
            <Route path="/legal" element={<LegalCombinedPage />} />
            <Route
              path="/login"
              element={
                WEB_AUTH_UI_ENABLED ? (
                  <Login />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/register"
              element={
                WEB_AUTH_UI_ENABLED ? (
                  <Register />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/redeem" element={<RedeemPage />} />
            <Route path="/supporters" element={<SupportersPage />} />

            {/* Web App Protected Routes (Moved to /app/*) */}
            <Route
              path="/app"
              element={
                <AuthGuard>
                  <Layout />
                </AuthGuard>
              }
            >
              <Route index element={<Navigate to="/app/profile" replace />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="verify-age" element={<AgeVerificationPage />} />
            </Route>

            {/* Full Screen App Routes */}

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route
                index
                element={<Navigate to="/admin/dashboard" replace />}
              />
              <Route path="dashboard" element={<AdminDashboardPage />} />
              <Route path="review" element={<IdentityReviewPage />} />
              <Route path="codes" element={<RedemptionCodesPage />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
